import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';

export const LoginPage: React.FC = () => {
  const { signIn, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        await signUp(email, password, displayName);
      } else {
        await signIn(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-dime-bg flex items-center justify-center px-4 py-10">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        <div className="absolute bottom-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-orange-500/5 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-dime-card/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-8 sm:p-10 text-white">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Logo size={56} variant="icon" />
            </div>
            <h1 className="text-4xl tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {isSignUp ? 'CREATE ACCOUNT' : 'WELCOME BACK'}
            </h1>
            <p className="text-sm text-gray-400 uppercase tracking-[0.2em]" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {isSignUp ? 'Join the shop' : 'Login Bay'}
            </p>
          </div>

          <p className="text-gray-400 text-center mb-8 text-sm leading-relaxed">
            {isSignUp
              ? 'Create your account to start receiving real-time welding coaching from Danny.'
              : 'Access the welding coach console. Keep your visor down and credentials tight.'}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isSignUp && (
              <div>
                <label
                  className="block text-sm mb-2 uppercase tracking-wider text-gray-400"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/10 transition-all"
                  placeholder="Your name"
                />
              </div>
            )}

            <div>
              <label
                className="block text-sm mb-2 uppercase tracking-wider text-gray-400"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/10 transition-all"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                className="block text-sm mb-2 uppercase tracking-wider text-gray-400"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/10 transition-all"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl uppercase tracking-[0.2em] font-medium bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError(null);
              }}
              className="w-full text-center text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {isSignUp ? (
                <>Already have an account? <span className="text-orange-500">Sign In</span></>
              ) : (
                <>Don't have an account? <span className="text-orange-500">Sign Up</span></>
              )}
            </button>
          </div>
        </div>

        {/* Back to Landing Link */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </a>
        </div>
      </div>
    </div>
  );
};
